<template>
  <div
    class="product-detail-content"
  >
    <form
      @submit.prevent="updateListingProduct"
      novalidate
    >
      <div class="product-detail">
        <Tab
          @tabClick="tabClick"
          :active-tab="activeTab"
        />
        <div v-if="product">
          <ProductInfo
            :product="product"
            ref="productInfo"
          />
          <ProductProperty
            :product="product"
            @setTabOffset="setTabOffset"
            @setDisabledValidationAttrs="setDisabledValidationAttrs"
            ref="productProperty"
          />
          <ProductSaleInfo
            :product="product"
            ref="productSaleInfo"
          />
          <ProductCargoInfo
            :product="product"
            ref="productCargoInfo"
            @setDeliveryPeriod="setDeliveryPeriod"
          />
        </div>
      </div>
      <div class="button-wrapper">
        <button
          class="btn btn-trendyol"
          type="submit"
        >
          GÜNCELLE
        </button>
      </div>
    </form>
  </div>
</template>
<script>
import service from '@/services/product';
import serviceSingleProduct from '@/services/singleProduct';
import serviceCategory from '@/services/category';
import mediaCenterService from '@/services/mediaCenter';
import messageHandler from '@/utils/message';
import InputMask from '@/directives/InputMask';
import format from '@/utils/format';
import setCaretPosition from '@/mixins/setCaretPosition';
import routerPush from '@/mixins/routerPush';
import Tab from './Tab';
import ProductInfo from './ProductInfo';
import mobile from '@/utils/mobile';
import ProductProperty from './ProductProperty';
import ProductSaleInfo from './ProductSaleInfo';
import ProductCargoInfo from './ProductCargoInfo';
import dayjs from 'dayjs';
import { scrollTo } from '@/utils/scrollTo';
import { uploadToOSS, uploadToS3 } from '@/utils/files';
import spinner from '@/utils/spinner';
import imageUtils from '@/utils/images';
import brandService from '@/services/brand';

const CDN_URL = 'https://cdn.dsmcdn.com/';

export default {
  name: 'ProductDetail',
  components: { ProductSaleInfo, ProductProperty, ProductInfo, Tab, ProductCargoInfo },
  data () {
    return {
      product: null,
      initialBarcode: null,
      categories: [],
      activeTab: 'productInfo',
      tabList: [],
      disabledValidationAttrs: [],
      mockedDeliveryDuration: false,
      deliveryPeriod: {},
    };
  },
  directives: {
    mask: InputMask,
  },
  computed: {
    hasMaxFixedPrice () {
      const now = dayjs().valueOf();
      const { campaignStartDate, campaignEndDate, campaignMaxPrice } = this.product;
      return campaignStartDate && campaignEndDate && campaignMaxPrice &&
        (campaignStartDate < now && campaignEndDate > now);
    },
    isBarcodeChanged () {
      return !(this.initialBarcode === this.product.barcode);
    },
  },
  mixins: [setCaretPosition, routerPush],
  methods: {
    async getProduct () {
      const productId = this.$route.params.productId;
      const product = await service.getProduct(productId);
      if (!product.deliveryDuration) {
        this.mockedDeliveryDuration = true;
      }
      const brands = await brandService.getBrandByName({
        name: product.brand,
        page: 0,
      });
      const currentBrand = brands.find(brand => brand.id === product.brandId);

      const productData = {
        ...product,
        attrs: {},
        images: product.images || [],
        salePrice: String(product.salePrice).replace(/\./g, ','),
        listPrice: String(product.listPrice).replace(/\./g, ','),
        category: {
          Id: product.pimCategoryId,
          Name: product.categoryName,
        },
        brand: {
          id: product.brandId,
          label: product.brand,
        },
        isPassiveBrand: currentBrand?.isPassive,
        vatRateOption: {
          label: `%${product.vatRate}`,
          value: product.vatRate,
        },
      };

      this.initialBarcode = productData.barcode;
      this.product = productData;
    },
    setDeliveryPeriod (deliveryPeriod) {
      this.deliveryPeriod = deliveryPeriod;
    },
    checkIfDeliveryDurationShouldUpdate (product) {
      return this.mockedDeliveryDuration && product.deliveryDuration === this.deliveryPeriod.agreedDeliveryTime;
    },
    async validate () {
      const product = this.product;

      const salePrice = format.priceToNumber(product.salePrice);
      const listPrice = format.priceToNumber(product.listPrice);

      if (listPrice < salePrice) {
        messageHandler.error('Trendyol Satış Fiyatı, Piyasa satış fiyatından büyük olamaz.');
        return false;
      }

      if (this.hasMaxFixedPrice) {
        if (salePrice > product.campaignMaxPrice) {
          messageHandler.error(`Kampanya dönemi boyunca kararlaştırmış olduğunuz max.
            fiyattan (${format.priceToNumber(product.campaignMaxPrice)}) fazla fiyat giremezsiniz.
            Detaylı bilgi için kategori yetkiliniz ile iletişime geçebilirsiniz.`);
          return false;
        }
      }
      if (product.images.length > 8) {
        messageHandler.error('Ürününüze en fazla 8 adet görsel ekleyebilirsiniz.');
        return false;
      }
      const isValidProductInfo = await this.$refs.productInfo.validate();
      const isValidProductSaleInfo = await this.$refs.productSaleInfo.validate();
      const isValidProductProperty = await this.$refs.productProperty.validate();
      const isValidCargoInfo = await this.$refs.productCargoInfo.validate();
      return isValidProductInfo && isValidProductSaleInfo &&
        isValidProductProperty && isValidCargoInfo;
    },
    async updateListingProduct () {
      const isValid = await this.validate();
      if (!isValid) {
        return false;
      }

      try {
        spinner.start();
        const product = { ...this.product };

        const filePrepareRequests = [];
        let productImages = [];
        product.images.forEach(image => {
          if (image.isFile) {
            const filePrepareRequest = new Promise((resolve, reject) => {
              serviceSingleProduct.getProductImagePath(image.name).then(async (signature) => {
                try {
                  await uploadToOSS(signature, image);
                  resolve(signature.downloadUrl);
                } catch (e) {
                  try {
                    const responseS3 = await mediaCenterService.prepareToUploadS3(image.name);
                    signature = responseS3[0];
                    await uploadToS3(signature.uploadUrl, image);
                    signature.objectKey = String(signature.downloadUrl).replace(CDN_URL, '');
                  } catch (e) {
                    reject(new Error('Bir hata oluştu şu anda resim yükleyemezsiniz'));
                  }
                }
              });
            });
            filePrepareRequests.push(filePrepareRequest);
          } else {
            productImages.push({
              url: imageUtils.clearCdnImage(image.url),
            });
          }
        });
        if (filePrepareRequests.length > 0) {
          const uploadResponse = await Promise.all(filePrepareRequests);
          const images = uploadResponse.map(item => ({ url: item }));
          productImages = [...productImages, ...images];
        }

        const data = {
          productMainId: format.stripTags(product.productMainId),
          title: format.stripTags(product.title),
          brand: product.brand.label,
          brandId: product.brand.id,
          categoryName: product.categoryName,
          stockCode: format.stripTags(product.stockCode),
          description: product.description,
          quantity: parseInt(product.quantity, 10),
          gender: product.gender,
          maxSaleQuantity: product.maxSaleQuantity,
          listPrice: format.priceToNumber(product.listPrice),
          salePrice: format.priceToNumber(product.salePrice),
          vatRate: parseInt(product.vatRateOption.value, 10),
          dimensionalWeight: parseFloat(product.dimensionalWeight),
          shipmentAddressId: product.shipmentAddress ? product.shipmentAddress.id : null,
          returningAddressId: product.returningAddress ? product.returningAddress.id : null,
          color: format.stripTags(product.color) || '',
          size: format.stripTags(product.size) || '',
          attributes: [],
          images: productImages,
          pimCategoryId: product.pimCategoryId,
          deliveryDuration: this.checkIfDeliveryDurationShouldUpdate(product) ? null : parseInt(product.deliveryDuration, 10),
        };

        if (!product.approved) {
          data.pimCategoryId = parseInt(product.category.Id);
          data.categoryName = product.category.Name;
          if (this.isBarcodeChanged) {
            Object.assign(data, { barcode: product.barcode });
          }
        }

        const attributes = Object.keys(product.attrs);
        for (let j = 0; j < attributes.length; j += 1) {
          const attrId = parseInt(attributes[j]);
          const attrObject = {
            attributeId: attrId,
          };
          if (this.product.attrs[attrId] && this.product.attrs[attrId].id) {
            attrObject.attributeValueId = this.product.attrs[attrId].id;
          } else if (typeof this.product.attrs[attrId] !== 'object') {
            attrObject.customAttributeValue = this.product.attrs[attrId];
          } else if (this.disabledValidationAttrs.indexOf(attrObject.attributeId) > -1) {
            const defaultAttr = this.product.attributes.filter(attr => attr.attributeId === attrObject.attributeId);
            if (defaultAttr.length && defaultAttr[0].attributeValueId) {
              attrObject.attributeValueId = defaultAttr[0].attributeValueId;
            } else if (defaultAttr.length && defaultAttr[0].attributeValue) {
              attrObject.customAttributeValue = defaultAttr[0].attributeValue;
            }
          } else {
            attrObject.attributeValueId = null;
          }

          data.attributes.push(attrObject);
        }

        const response = await service.saveProduct(product.id, data);
        if (response && response.id && this.product.id !== response.id) {
          this.product.id = response.id;
          this.$router.push(`/products/${response.id}`);
        }
        if (product.badgePrices) {
          const { maxPrice, minPrice } = product.badgePrices;

          if (data.salePrice > 0 && maxPrice && data.salePrice > maxPrice) {
            messageHandler.warning('Talebiniz alınmıştır. Ürün fiyatı, bu ürüne özgü hesapladığımız maksimum fiyattan yüksektir. Ürününüz bu sebepten kitlenebilir.');
          } else if (data.salePrice > 0 && minPrice && data.salePrice < minPrice) {
            messageHandler.warning('Talebiniz alınmıştır. Ürün fiyatı, bu ürüne özgü hesapladığımız minimum fiyattan düşüktür. Ürününüz bu sebepten kitlenebilir.');
          } else {
            messageHandler.success('Talebiniz başarıyla alınmıştır.');
          }
        } else {
          messageHandler.success('Talebiniz başarıyla alınmıştır.');
        }

        this.$root.trackEvent({
          eventCategory: 'MobileAppListing',
          eventAction: 'üründetayurunguncelleme',
          eventLabel: 'üründetayurunguncelleme',
        });
        setTimeout(() => {
          spinner.done();
          mobile.navigationNavigate({
            pageName: 'Product',
          });
        }, 2000);
      } catch (e) {
        messageHandler.error(e.message || 'Bir Hata oluştu.');
      }
    },
    routeMapper (route) {
      const routeMap = {
        ListingAll: 'all',
        ListingRejected: 'rejected',
        ListingOutOfStock: 'out-of-stock',
        ListingOnSale: 'on-sale',
        ListingNotOnSale: 'not-on-sale',
        ListingActionNeeded: 'action-needed',
      };
      return routeMap[route];
    },
    async getCategoryAttributes () {
      this.attributes = await serviceCategory.getCategoryAttributes(this.product.pimCategoryId);
    },
    tabClick (tab) {
      this.activeTab = tab.id;
      const activeTab = this.tabList.filter(t => t.id === tab.id)[0];
      const wrapper = document.querySelector('.product-detail-content');
      scrollTo(wrapper, {
        end: activeTab.offsetTop + 5,
      });
    },
    onScroll (e) {
      const { scrollTop } = e.target;
      this.tabList.forEach(list => {
        if (list.offsetTop <= scrollTop && scrollTop < list.offsetBottom) {
          this.activeTab = list.id;
        }
      });
    },
    setTabOffset () {
      const sections = Array.from(document.querySelectorAll('section'));
      sections.forEach(section => {
        const offsetTop = section.offsetTop - 55;
        this.tabList.push({
          offsetTop: offsetTop,
          offsetBottom: offsetTop + section.offsetHeight,
          id: section.id,
        });
      });
    },
    setDisabledValidationAttrs (attrs) {
      this.disabledValidationAttrs = attrs;
    },
  },
  async created () {
    await this.getProduct();
  },
};

</script>
<style lang="scss">
  .product-detail-content {
    background-color: #ffffff;
    height: calc(100vh);
    overflow: hidden;
    overflow-y: auto;
    position: relative;
    padding: 55px 15px 90px;

    section {
      display: block;
      margin-bottom: 25px;
      &:last-child {
        margin-bottom: 0;
      }

      .header {
        font-size: 16px;
        color: #273142;
        font-weight: 500;
        margin-bottom: 15px;
      }

      .row {
        margin-bottom: 15px;

        .label {
          font-size: 12px;
          color: #273142;
          font-weight: 500;
          margin-bottom: 5px;
          display: block;

          .required {
            color: #FF5043;
          }
        }

        .form-control {
          border-color: #DEE0EB;

          &.disabled,
          &[disabled] {
            background-color: #F1F2F7;
          }
          &.contenteditable{
            max-height: 100px;
            overflow: hidden;
            overflow-y: auto;
          }
        }

        .is-invalid > .input-group .form-control,
        .form-control.is-invalid {
          border-color: #FF5043;
        }

        & .invalid-feedback {
          font-size: 11px;
          color: #FF5043;
        }
      }
    }

    .button-wrapper {
      position: fixed;
      left: 0;
      bottom: 0;
      width: 100%;
      padding: 15px;
      margin-top: 15px;
      background: #ffffff;
      border-radius: 10px 10px 0 0;
      box-shadow: 0 -5px 7px rgba(39, 49, 66, 0.1);

      display: flex;

      .btn {
        flex: 1;

        & + .btn {
          margin-left: 10px;
        }
      }
    }
  }
</style>
